import React, { useState, useEffect } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "../style.scss";
import { useDispatch } from "react-redux";
import { updateCallForwarding } from "data/user/actions";
import { formatPhone } from "data/user/constants";
import ToastNotification from "../../components/ToastNotification";
import LoadingButton from "components/LoadingButton/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormikText from "../components/Formik/FormikText";
import { getMarketingProfileValidationRules } from "../../../utils/validation";

const CallForward = ({ isOpen, onClose, modalType, callSettingId, currentForwardingNumber, userID }) => {
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationRules = getMarketingProfileValidationRules();
  const [initialValues, setInitialValues] = useState({ phone: "" });

  // Validation schema for phone number
  const validationSchema = Yup.object().shape({
    phone: Yup.string().required("")
      .test(
        "is-valid-phone",
        "This phone number is invalid. Please try another one.",
        (value) => {
          if (!value) return true; // Skip test if empty
          const digitsOnly = value.replace(/\D/g, "");
          return digitsOnly.length === 10;
        }
      ),
  });

  // Update initial values when modal opens or currentForwardingNumber changes
  useEffect(() => {
    if (isOpen) {
      if (modalType === "change" && currentForwardingNumber) {
        // Extract digits and format for display when changing
        const digits = currentForwardingNumber.replace(/\D/g, "");
        if (digits.length === 10) {
          setInitialValues({ phone: formatPhone(digits) });
        } else {
          setInitialValues({ phone: digits });
        }
      } else {
        setInitialValues({ phone: "" });
      }
    }
  }, [isOpen, modalType, currentForwardingNumber]);

  const handleInternalClose = () => {
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async (values) => {
    const digits = values.phone.replace(/\D/g, "");

    setIsSubmitting(true);

    try {
      const result = await dispatch(updateCallForwarding(callSettingId, digits, userID));

      if (result && result.success) {
        setToastMessage("Your call forwarding number has been successfully updated.");
        setToastType("success");
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
          handleInternalClose();
        }, 2000);
      } else {
        setToastMessage("Something went wrong. Please try again.");
        setToastType("error");
        setShowToast(true);
        setIsSubmitting(false);
        handleInternalClose();
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
      }
    } catch (err) {
      setToastMessage("Something went wrong. Please try again.");
      setToastType("error");
      setShowToast(true);
      setIsSubmitting(false);
      handleInternalClose();
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        isCloseButton={false}
        uniqId="CallForwardModal"
        width="auto"
        borderRadius="12px"
        modalClassName={classNames(
          css.batchDialerModal,
          css.batchDialerAutoWidth
        )}
        className={classNames(css.clsAccPageModals, css.clsBillingModal)}
        onClose={onClose}
      >
        <Formik
          key={isOpen ? `call-forward-${modalType}-${currentForwardingNumber || 'new'}` : 'call-forward-closed'}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ touched, errors, handleSubmit, values }) => {
            const phoneDigits = values.phone ? values.phone.replace(/\D/g, "") : "";
            const isValid = phoneDigits.length === 10;

            return (
              <Form onSubmit={handleSubmit}>
                <div className={css.modalContainer} style={{ width: "auto" }}>
                  <div className={css.headertxt}>
                    <span>
                      Call Forwarding Number
                    </span>
                  </div>
                  <div className={css.instructionText}>
                    <span>
                      When a lead calls you back, the call will be
                    </span>
                    <br />
                    <span>forwarded to this number.</span>
                  </div>
                  <div className={classNames(css.clsFormGroup, css.mb_24)}>
                    <FormikText
                      name="phone"
                      placeholder="Enter Phone Number"
                      errors={errors}
                      touched={touched}
                      required={false}
                      maxLength={validationRules.maxLength.businessPhone}
                      validationPattern={validationRules.validationPattern.businessPhone}
                      disabled={isSubmitting}
                      phone
                    />
                  </div>
                  <div className={css.buttonWrapper}>
                    <button
                      className={css.cancelBtn}
                      onClick={handleInternalClose}
                      disabled={isSubmitting}
                      type="button"
                    >
                      <span className={css.cancelBtnLabel}>Cancel</span>
                    </button>
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                      variant="teal"
                      className={css.confirmModalBtn}
                      type="submit"
                    >
                      <span className={css.confirmBtnLabel}>Confirm</span>
                    </LoadingButton>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {showToast && (
        <ToastNotification
          message={toastMessage}
          show={true}
          type={toastType}
        />
      )}
    </>
  );
};

export default CallForward;
